import type { OverviewModuleKey } from "./Overview";

export interface OverviewSkeletonProps {
  collapsed: Record<OverviewModuleKey, boolean>;
}

const bar = (width: number | string = 80, height = 10) => (
  <span className="skeleton-piece" style={{ display: "inline-block", width, height }} />
);
const heading = () => <div className="section-heading"><div style={{ width: "100%" }}>{bar("35%", 12)}<div>{bar("20%", 8)}</div></div></div>;
const chart = () => <div className="skeleton-piece skeleton-chart-placeholder" style={{ height: 220, opacity: .4 }} />;

/** Follow Overview's broad layout without predicting its data or selected chart style. */
export default function OverviewSkeleton({ collapsed, label = "Loading overview" }: OverviewSkeletonProps & { label?: string }) {
  const moduleClass = (module: OverviewModuleKey, base: string) => `${base} overview-module${collapsed[module] ? " is-collapsed" : ""}`;
  return (
    <div className="loading-skeleton" role="status" aria-label={label} aria-busy="true" style={{ padding: 0 }}>
      <div className="overview-modules" aria-hidden="true">
        <div className="overview-insights-link">
          {bar(24, 24)}<div style={{ flex: 1 }}>{bar("30%", 12)}<div>{bar("70%", 8)}</div></div>
        </div>
        <section className={moduleClass("daily", "spending-chart-container")}>
          {heading()}{!collapsed.daily && chart()}
        </section>
        <section className={moduleClass("rhythm", "spending-rhythm")}>
          <div className="spending-rhythm-heading">{bar("35%", 12)}</div>
          <div className="spending-rhythm-grid">
            {[0, 1, 2, 3, 4, 5].map(index => <div key={index}><span>{bar("65%", 8)}</span><strong>{bar("50%", 14)}</strong></div>)}
          </div>
        </section>
        <section className={moduleClass("monthly", "spending-card")}>
          <div className="card-topline">{bar("40%")}</div>
          <div className="spending-number">{bar("55%", 28)}</div>
          <div className="spending-caption">{bar(130)}</div>
          <div className="spending-bottom">{bar("40%")}{bar(70)}</div>
        </section>
        <section className={moduleClass("categories", "panel category-panel")}>
          {heading()}{chart()}
        </section>
        <section className={moduleClass("position", "position-card")}>
          {heading()}
          {[0, 1, 2].map(index => <div className="position-line" key={index}>{bar("50%")}{bar(70)}</div>)}
          <div className="net-position">{bar("40%", 14)}{bar(70, 14)}</div>
        </section>
        {(["activity", "upcoming"] as const).map((module, index) => <section key={module} className={moduleClass(module, index ? "panel upcoming-panel" : "panel activity-panel")}>
          {heading()}
          {[0, 1, 2].map(row => <div key={row} className="skeleton-row"><div className="skeleton-avatar skeleton-piece" /><div><div className="skeleton-name skeleton-piece" /><div className="skeleton-caption skeleton-piece" /></div><div className="skeleton-piece" /></div>)}
        </section>)}
      </div>
    </div>
  );
}
